import React, { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { Spinner } from './Spinner';

/**
 * `common/Button` (M-UX-2.3, architecture-ux-restructure.md §4.3).
 *
 * Beseitigt strukturell die ~85 handkopierten Button-Klassenlisten im
 * Bestandscode — jede Nutzung dieser Komponente ist automatisch on-brand
 * und barrierearm. `variant="primary"` erzwingt Navy-Text (`text-accent-fg`,
 * token `--accent-fg`) auf Mint-Hintergrund **per Implementierung** (fester
 * Bestandteil der Variant-Klassen, nicht optional/konventionsabhängig) —
 * das ist die AA-Kontrast-Vorgabe aus §4.3.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner in place of the icon and disables the button while true. */
  loading?: boolean;
  icon?: ReactNode;
}

// 'xs' (M-A, architecture-ux-density-v2.md §5.4) — compact inline row action size.
const SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'px-2.5 py-1 text-xs gap-1',
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-5 py-2.5 text-base gap-2',
};

const SPINNER_SIZE: Record<ButtonSize, 'sm' | 'md'> = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'md',
};

// primary = Mint bg + Navy text baked into the variant, not left to callers (AA contrast).
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-700 text-accent-fg border border-transparent',
  secondary: 'bg-surface-inset hover:bg-surface text-primary border border-default',
  ghost: 'bg-transparent hover:bg-surface-raised text-secondary border border-transparent',
  danger: 'bg-red-600 hover:bg-red-700 active:bg-red-700 text-white border border-transparent',
};

/**
 * Central button primitive. Always renders a native `<button>` — pass
 * `type="submit"` explicitly where needed, default is `"button"` to avoid
 * accidental form submits (the #1 source of the copy-pasted variants).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, icon, disabled, className = '', children, type, ...rest },
  ref
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center font-medium rounded-control transition-colors duration-150',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? (
        <Spinner size={SPINNER_SIZE[size]} />
      ) : icon ? (
        <span className="shrink-0" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
});

export default Button;
